import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Check, ChevronDown, ChevronUp, GripVertical, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import type { ContourStage, ContourTheme } from '../../api/contour';
import { useDragReorder } from '../../hooks/useDragReorder';

interface StageListProps {
  theme: ContourTheme;
  startIndex?: number;
  editMode: boolean;
  disabled: boolean;
  highlightStageId: string | null;
  openMenuId: string | null;
  onToggle: (stage: ContourStage) => void;
  onMove: (stageId: string, direction: -1 | 1) => void;
  onReorder: (ids: string[]) => void;
  onOpenMenu: (id: string | null) => void;
  onEdit: (stage: ContourStage) => void;
  onDelete: (stage: ContourStage) => void;
}

/**
 * Список этапов темы. В режиме редактирования поддерживает перетаскивание
 * (Pointer Events — работает пальцем на iOS) и стрелки как альтернативу.
 */
export function StageList({
  theme,
  startIndex = 0,
  editMode,
  disabled,
  highlightStageId,
  openMenuId,
  onToggle,
  onMove,
  onReorder,
  onOpenMenu,
  onEdit,
  onDelete,
}: StageListProps) {
  const ids = useMemo(() => theme.stages.map((stage) => stage.id), [theme.stages]);
  const commit = useCallback((next: string[]) => onReorder(next), [onReorder]);
  const { order, activeId, offsetY, beginDrag, registerItem } = useDragReorder(ids, commit);
  const highlightedRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (highlightStageId) highlightedRef.current?.focus();
  }, [highlightStageId]);

  const byId = useMemo(
    () => new Map<string, ContourStage>(theme.stages.map((stage) => [stage.id, stage])),
    [theme.stages],
  );
  const visible = editMode ? order : ids;

  return (
    <ul className="contour-list contour-list--stages" aria-label="Этапы">
      {visible.map((id, index) => {
        const stage = byId.get(id);
        if (!stage) return null;
        const isActive = activeId === id;

        return (
          <li
            key={id}
            ref={(element) => {
              registerItem(id, element);
              if (highlightStageId === id) highlightedRef.current = element;
            }}
            tabIndex={highlightStageId === id ? -1 : undefined}
            className={`contour-stage ${editMode ? 'is-editing' : ''} ${stage.completed ? 'is-completed' : ''} ${
              highlightStageId === id ? 'is-highlighted' : ''
            } ${isActive ? 'is-dragging' : ''}`}
            style={isActive ? { transform: `translateY(${offsetY}px)` } : undefined}
          >
            {editMode ? (
              <button
                className="contour-stage__remove"
                type="button"
                disabled={disabled}
                onClick={() => onDelete(stage)}
                aria-label={`Удалить этап: ${stage.name}`}
              >
                <span aria-hidden="true">−</span>
              </button>
            ) : (
              <button
                className="contour-stage__toggle"
                type="button"
                disabled={disabled}
                onClick={() => onToggle(stage)}
                aria-pressed={stage.completed}
                aria-label={
                  stage.completed ? `Снять отметку: ${stage.name}` : `Отметить выполненным: ${stage.name}`
                }
              >
                {stage.completed ? <Check size={16} strokeWidth={3} aria-hidden="true" /> : null}
              </button>
            )}

            <span className="contour-stage__copy">
              <strong>
                {startIndex + index + 1}. {stage.name}
              </strong>
              {stage.description ? <span>{stage.description}</span> : null}
              <span className="contour-stage__meta">
                {{ Todo: 'К выполнению', InProgress: 'В работе', Blocked: 'Заблокировано', Done: 'Выполнено', Cancelled: 'Отменено' }[stage.status]}
                {' · '}
                {{ Low: 'Низкий', Normal: 'Обычный', High: 'Высокий', Urgent: 'Срочный' }[stage.priority]}
                {stage.deadlineUtc ? ` · до ${new Date(stage.deadlineUtc).toLocaleString('ru-RU')}` : ''}
              </span>
            </span>

            {editMode ? (
              <span className="contour-stage__reorder">
                <button
                  type="button"
                  disabled={disabled || index === 0}
                  onClick={() => onMove(id, -1)}
                  aria-label="Переместить выше"
                >
                  <ChevronUp size={18} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  disabled={disabled || index === visible.length - 1}
                  onClick={() => onMove(id, 1)}
                  aria-label="Переместить ниже"
                >
                  <ChevronDown size={18} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="contour-stage__grip"
                  disabled={disabled}
                  onPointerDown={(event) => {
                    if (!disabled) beginDrag(id, event);
                  }}
                  aria-label={`Перетащить: ${stage.name}`}
                >
                  <GripVertical size={18} aria-hidden="true" />
                </button>
              </span>
            ) : (
              <span className="contour-menu-anchor">
                <button
                  className="contour-stage__menu"
                  type="button"
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenMenu(openMenuId === id ? null : id);
                  }}
                  aria-label={`Действия: ${stage.name}`}
                  aria-expanded={openMenuId === id}
                >
                  <MoreVertical size={18} aria-hidden="true" />
                </button>
                {openMenuId === id && (
                  <div className="contour-menu" role="menu" onClick={(event) => event.stopPropagation()}>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        onOpenMenu(null);
                        onEdit(stage);
                      }}
                    >
                      <Pencil size={16} aria-hidden="true" />
                      Изменить
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      disabled={index === 0}
                      onClick={() => {
                        onOpenMenu(null);
                        onMove(id, -1);
                      }}
                    >
                      <ChevronUp size={16} aria-hidden="true" />
                      Переместить выше
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      disabled={index === visible.length - 1}
                      onClick={() => {
                        onOpenMenu(null);
                        onMove(id, 1);
                      }}
                    >
                      <ChevronDown size={16} aria-hidden="true" />
                      Переместить ниже
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="is-danger"
                      onClick={() => {
                        onOpenMenu(null);
                        onDelete(stage);
                      }}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                      Удалить
                    </button>
                  </div>
                )}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
